/**
 * Window.ts
 *
 * Module wrapping around the global window object
 */

import { TelemetryEvent, TelemetrySink } from "@microsoft/oteljs";
import { IFloodgateSettingIdMap, IFloodgateStorageProvider, ISurveyForm } from "@ms-ofb/floodgate-shared-typings";
import { FloodgateEngine } from "@ms-ofb/officefloodgatecore";
import { ICustomUXHandler } from "../ICustomUXHandler";
import { IOverrideRenderCallback } from "../IOverrideRenderCallback";
import * as Configuration from "./../Configuration/Configuration";
import { ILaunchOptionsInAppFeedback } from "./../Configuration/ILaunchOptions";
import { FeedbackStatus } from "./../Constants";
import ICustomSurvey from "./../FloodgateCore/ICustomSurvey";
import IUISurvey from "./../FloodgateCore/UISurvey/IUISurvey";

const w = window as any;
const officeBrowserFeedback = w.OfficeBrowserFeedback = w.OfficeBrowserFeedback || {};
const floodgate = officeBrowserFeedback.floodgate = officeBrowserFeedback.floodgate || {};

/**
 * Get the IWindow object
 */
export function get(): IWindow { return w; }

/**
 * Get the IWindow object
 */
export function getGlobal(): IWindow { return w; }

/**
 * Set the processTelemetryEvent() method
 * @param processTelemetryEvent the method
 */
export function setProcessTelemetryEvent(
    processTelemetryEvent: (telemetryEvent: TelemetryEvent) => void) {
    officeBrowserFeedback.processTelemetryEvent = processTelemetryEvent;
}

/**
 * Set the sendUserFact() method
 * @param sendUserFact the method
 */
export function setSendUserFact(
    sendUserFact: (userFactName: string) => void) {
    officeBrowserFeedback.sendUserFact = sendUserFact;
}

/**
 * Set the setUiStrings() method
 * @param setUiStrings the method
 */
export function setSetUiStrings(setUiStrings: (data: any) => void) {
    officeBrowserFeedback.setUiStrings = setUiStrings;
}

/**
 * Set the singleFeedback() method
 * @param singleFeedback the method
 */
export function setSingleFeedback(
    singleFeedback: (feedbackType: string, launchOptions: ILaunchOptionsInAppFeedback) => Promise<any>) {
    officeBrowserFeedback.singleFeedback = singleFeedback;
}

/**
 * Set the multiFeedback() method
 * @param multiFeedback the method
 */
export function setMultiFeedback(
    multiFeedback: (launchOptions: ILaunchOptionsInAppFeedback) => Promise<any>) {
    officeBrowserFeedback.multiFeedback = multiFeedback;
}

/**
 * Set the getFeedbackStatus() method
 * @param getFeedbackStatus the method
 */
export function setFeedbackStatus(
    getFeedbackStatus: () => FeedbackStatus) {
    officeBrowserFeedback.getFeedbackStatus = getFeedbackStatus;
}

/**
 * Set the createScreenshot() method
 * @param createScreenshot the method
 */
export function setCreateScreenshot(
    createScreenshot: (domElement?: object, options?: any) => Promise<any>) {
    officeBrowserFeedback.createScreenshot = createScreenshot;
}

/**
 * Set the isFeedbackSurveyEnabledByAADC() method
 * @param isFeedbackSurveyEnabledByAADC the method
 */
export function setIsFeedbackSurveyEnabledByAADC(
    isFeedbackSurveyEnabledByAADC: () => boolean) {
    officeBrowserFeedback.isFeedbackSurveyEnabledByAADC = isFeedbackSurveyEnabledByAADC;
}

/**
 * Set the floodgate customUXHandler interface
 * @param floodgateCustomUXHandler the interface
 */
export function setFloodgateCustomUXHandler(floodgateCustomUXHandler: ICustomUXHandler) {
    floodgate.customUXHandler = floodgateCustomUXHandler;
}

/**
 * Set the floodgate showSurvey() method
 * @param floodgateShowSurvey the method
 */
export function setFloodgateShowSurvey(
    floodgateShowSurvey: (survey: IUISurvey, overrideUXRenderCallbackAsync?: IOverrideRenderCallback) => Promise<any>
) {
    floodgate.showSurvey = floodgateShowSurvey;
}

/**
 * Set the floodgate showFormSurvey() method
 * @param floodgateShowFormSurvey the method
 */
export function setFloodgateShowFormSurvey(
    floodgateShowFormSurvey: (survey: ISurveyForm, overrideUXRenderCallbackAsync?: IOverrideRenderCallback) => Promise<any>
) {
    floodgate.showFormSurvey = floodgateShowFormSurvey;
}

/**
 * Set the floodgate showCustomSurvey() method
 * @param floodgateShowCustomSurvey the method
 */
export function setFloodgateShowCustomSurvey(floodgateShowCustomSurvey: (survey: ICustomSurvey) => Promise<any>) {
    floodgate.showCustomSurvey = floodgateShowCustomSurvey;
}

/**
 * Set the floodgate initialize() method
 * @param floodgateInitialize the method
 */
export function setFloodgateInitialize(floodgateInitialize: () => Promise<any>) {
    floodgate.initialize = floodgateInitialize;
}

/**
 * Set the floodgate start() method
 * @param floodgateStart the method
 */
export function setFloodgateStart(floodgateStart: () => Promise<any>) {
    floodgate.start = floodgateStart;
}

/**
 * Set the floodgate stop() method
 * @param floodgateStop the method
 */
export function setFloodgateStop(floodgateStop: () => void) {
    floodgate.stop = floodgateStop;
}

/**
 * Set the floodgate getEngine() method
 * @param floodgateGetEngine the method
 */
export function setFloodgateGetEngine(floodgateGetEngine: () => FloodgateEngine) {
    floodgate.getEngine = floodgateGetEngine;
}

/**
 * Set the floodgate updateTheme() method
 * @param floodgateUpdateTheme the method
 */
export function setFloodgateUpdateTheme(floodgateUpdateTheme: (themeConfig: Configuration.IDynamicUxThemeConfig) => Promise<any>) {
    floodgate.updateTheme = floodgateUpdateTheme;
}

/**
 * Set the floodgate updateTelemetrySink() method
 */
export function setUpdateTelemetrySink(updateSink: (sink: TelemetrySink) => void) {
    officeBrowserFeedback.updateTelemetrySink = updateSink;
}

/**
 * Set the floodgate getSettingIdMap() method
 * @param floodgateSettingIdMap the method
 */
export function getSettingIdMap(floodgateSettingIdMap: () => IFloodgateSettingIdMap) {
    floodgate.getSettingIdMap = floodgateSettingIdMap;
}

export function setStorageProvider(getStorageProvider: () => IFloodgateStorageProvider) {
    floodgate.getStorageProvider = getStorageProvider;
}

export interface IInitOptions extends Configuration.IInitOptionsCommon, Configuration.IInitOptionsInAppFeedback {
}

export interface IFloodgate {
    initOptions: Configuration.IInitOptionsFloodgate;
    customUXHandler: ICustomUXHandler;
    showSurvey: (survey: IUISurvey, overrideUXRenderCallbackAsync?: IOverrideRenderCallback) => Promise<any>;
    showFormSurvey: (survey: ISurveyForm, overrideUXRenderCallbackAsync?: IOverrideRenderCallback) => Promise<any>;
    showCustomSurvey: (survey: ICustomSurvey) => Promise<any>;
    initialize: () => Promise<any>;
    start: () => Promise<any>;
    stop: () => void;
    getEngine: () => FloodgateEngine;
    updateTheme: (themeConfig: Configuration.IDynamicUxThemeConfig) => Promise<any>;
    getStorageProvider: () => IFloodgateStorageProvider;
}

export interface IWindow extends WindowLocalStorage {
    OfficeBrowserFeedback: {
        initOptions: IInitOptions;
        multiFeedback: (launchOptions: ILaunchOptionsInAppFeedback) => Promise<any>;
        sdkVersion: string;
        processTelemetryEvent: (telemetryEvent: TelemetryEvent) => void;
        sendUserFact: ((userFactName: string) => void);
        singleFeedback: (feedbackType: string, launchOptions: ILaunchOptionsInAppFeedback) => Promise<any>;
        setUiStrings: (data: any) => void;
        html2canvas: () => ((domElement?: object, options?: any) => Promise<HTMLCanvasElement>);
        createScreenshot: (domElement?: object, options?: any) => Promise<HTMLCanvasElement>;
        floodgate: IFloodgate;
        isFeedbackSurveyEnabledByAADC: () => boolean;
        getFeedbackStatus: () => FeedbackStatus;
        updateTelemetrySink: (sink: TelemetrySink) => void;
    };
}